import React, { useEffect, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Box, Button, Chip, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import { AddBlockButton } from './AddBlock';
import { ColumnGroups, FollowsFilters, ReadsQueries, ShownWhen, ValuePills } from './BlockExtras';
import { usePanePresentation } from './PaneSettingsLayout';
import PartSettings from './PartSettings';
import { SeriesColours } from './SeriesColour';
import QuerySettings from './QuerySettings';
import RawSettings, { replaceSetting } from './RawSettings';
import { PRESETS, SIZED, heightPatch, minContentFor, presetsFor } from './ResizeHandle';
import { Choice, PanelHeader, Picker, Row, Section, SettingScope, SettingsFind, SizeSetting, SnapSlider, SubHeading, SwitchRow, TextInput, TextRow, TextSizeSetting } from './settingsKit';
import ViewCode from './ViewCode';
import { type BoardOperation, SPANS, suitableForms } from './editor';
import { humanise, memberLabel } from './format';
import { plainTitle } from './titles';
import { type Block, type BlockFamily, type EnvelopeColumn, type KpiItem, type LensDocument, type LensDocumentData, type LensState, type LensView, blockKind } from './types';
import type { BoardEditor } from './useBoardEdit';

type BlockSettingsProps = {
  block?: Block;
  kind?: string;
  family?: BlockFamily;
  data?: LensDocumentData;
  editor: BoardEditor;
  item?: string;
  view?: LensView;
  siblings?: Array<Block>;
  onSelect?: (id: string) => void;
  state?: LensState;
  lens?: LensDocument;
  howItsMade?: React.ReactNode;
};

type Group = 'data' | 'chart' | 'appearance';

type Choice = { label: string; key: string; group: Group; choices: Array<[string, string]>; keywords?: Array<string>; families?: Array<BlockFamily>; parse?: (value: string) => unknown; clears?: Array<string>; grained?: boolean };

const INHERIT = 'inherit';

const CHOICES: Array<Choice> = [
  {
    label: 'Smooth',
    key: 'rolling',
    group: 'chart',
    choices: [
      [INHERIT, 'Off'],
      ['7', '7'],
      ['28', '28'],
    ],
    families: ['time'],
    parse: Number,
    clears: ['cumulative'],
    grained: true,
  },
  {
    label: 'Running total',
    key: 'cumulative',
    group: 'chart',
    choices: [
      [INHERIT, 'Off'],
      ['true', 'On'],
    ],
    families: ['time'],
    parse: () => true,
    clears: ['rolling'],
    grained: true,
  },
  {
    label: 'Marker',
    key: 'mark',
    group: 'chart',
    choices: [
      [INHERIT, 'Auto'],
      ['peak', 'Peak'],
      ['latest', 'Latest'],
      ['none', 'None'],
    ],
    families: ['time'],
  },
  {
    label: 'Labels',
    key: 'labels',
    group: 'chart',
    choices: [
      [INHERIT, 'Auto'],
      ['end', 'At the end'],
      ['legend', 'Legend'],
      ['none', 'None'],
    ],
    families: ['time'],
  },
  {
    label: 'Values on the chart',
    key: 'values',
    group: 'chart',
    choices: [
      [INHERIT, 'None'],
      ['latest', 'Latest'],
      ['top', 'Top 3'],
      ['all', 'Every point'],
    ],
    families: ['time'],
  },
  {
    label: 'Lines',
    key: 'curve',
    group: 'chart',
    choices: [
      [INHERIT, 'Straight'],
      ['smooth', 'Smooth'],
    ],
    families: ['time'],
  },
  {
    label: 'Change as',
    key: 'delta',
    group: 'chart',
    choices: [
      [INHERIT, 'Lens'],
      ['pct', '%'],
      ['diff', 'Difference'],
      ['both', 'Both'],
      ['none', 'Off'],
    ],
  },
  {
    label: 'Compared values',
    key: 'baseline',
    group: 'chart',
    choices: [
      [INHERIT, 'Lens'],
      ['true', 'Show'],
      ['false', 'Hide'],
    ],
  },
  {
    label: 'Numbers',
    key: 'numbers',
    group: 'chart',
    choices: [
      [INHERIT, 'Lens'],
      ['compact', 'A$28.4M'],
      ['full', 'In full'],
    ],
  },
  {
    label: 'Show trend line',
    key: 'spark',
    group: 'chart',
    keywords: ['sparkline', 'spark'],
    choices: [
      [INHERIT, '13 points'],
      ['0', 'Off'],
      ['8', '8 points'],
      ['26', '26 points'],
    ],
    families: ['figures'],
    parse: Number,
  },
  {
    label: 'Status colour',
    key: 'status',
    group: 'chart',
    choices: [
      [INHERIT, 'Off'],
      ['border', 'On'],
    ],
    families: ['figures'],
  },
  {
    label: 'Parts',
    key: 'stack',
    group: 'chart',
    choices: [
      [INHERIT, 'Side by side'],
      ['true', 'Stacked'],
    ],
    families: ['time', 'breakdown'],
    parse: () => true,
  },
  {
    label: 'Click to filter',
    key: 'select',
    group: 'data',
    choices: [
      [INHERIT, 'On'],
      ['false', 'Off'],
    ],
    families: ['breakdown', 'table'],
  },
  {
    label: 'Totals row',
    key: 'totals',
    group: 'data',
    choices: [
      [INHERIT, 'Show'],
      ['false', 'Hide'],
    ],
    families: ['table'],
  },
  {
    label: 'Subtitle',
    key: 'subtitle',
    group: 'appearance',
    choices: [
      [INHERIT, 'Show'],
      ['false', 'Hide'],
    ],
  },
  {
    label: 'Tone',
    key: 'tone',
    group: 'appearance',
    choices: [
      [INHERIT, 'Note'],
      ['good', 'Good'],
      ['warning', 'Warning'],
      ['critical', 'Critical'],
    ],
    families: ['prose'],
  },
  {
    label: 'Sort',
    key: 'sort',
    group: 'data',
    choices: [
      [INHERIT, 'By value'],
      ['label', 'In order'],
    ],
    families: ['breakdown', 'table', 'time'],
  },
  {
    label: 'Title',
    key: 'titleAlign',
    group: 'appearance',
    choices: [
      [INHERIT, 'Left'],
      ['center', 'Centred'],
    ],
  },
  {
    label: 'Frame',
    key: 'frame',
    group: 'appearance',
    choices: [
      [INHERIT, 'Lens'],
      ['none', 'None'],
      ['hairline', 'Line'],
      ['tile', 'Box'],
    ],
  },
];

const TOPPED: Array<BlockFamily> = ['time', 'breakdown', 'table'];

export function TextSetting({
  label,
  value,
  multiline,
  placeholder,
  onCommit,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  placeholder?: string;
  onCommit: (next: string) => void;
}) {
  return <TextRow label={label} multiline={multiline} onCommit={onCommit} placeholder={placeholder} set={Boolean(value)} value={value} />;
}

const DECIMALS: Array<[string, string]> = [
  [INHERIT, 'Auto'],
  ['0', '0'],
  ['1', '1'],
  ['2', '2'],
  ['3', '3'],
];

function blockMeasures(block: Block, data?: LensDocumentData): Array<EnvelopeColumn> {
  const planned = data?.blocks[block.id];
  const columns = (planned?.queries ?? (planned?.query ? [planned.query] : []))
    .flatMap((id) => data?.queries[id]?.columns ?? [])
    .filter((column) => column.role === 'measure');
  return columns.filter((column, index) => columns.findIndex((other) => other.name === column.name) === index);
}

function MeasureSettings({ column, editor }: { column: EnvelopeColumn; editor: BoardEditor }) {
  const define = (def: Record<string, unknown>) => editor.apply({ op: 'measure', id: column.name, def });
  return (
    <SettingScope value={{ id: column.name, label: column.label }}>
      <SubHeading>{column.label}</SubHeading>
      <TextRow label="Label" onCommit={(label) => label && define({ label })} value={column.label} />
      <Choice
        choices={DECIMALS}
        disabled={editor.pending}
        inherit={INHERIT}
        label="Decimals"
        name={`Decimals for ${column.label}`}
        onChange={(value) => define({ decimals: value === INHERIT ? null : Number(value) })}
        value={column.decimals == null ? INHERIT : String(column.decimals)}
      />
      <Row keywords={MEASURE_FORMATS.map(([, text]) => text)} label="Shown as">
        <Picker choices={MEASURE_FORMATS} disabled={editor.pending} label="Shown as" onChange={(format) => define({ format })} value={column.format ?? 'num1'} />
      </Row>
      <SwitchRow checked={Boolean(column.lowerIsBetter)} disabled={editor.pending} label="Lower is better" onChange={(checked) => define({ lowerIsBetter: checked || null })} />
    </SettingScope>
  );
}

const MEASURE_FORMATS: Array<[string, string]> = [
  ['aud', 'A$'],
  ['usd', 'US$'],
  ['int', 'Whole number'],
  ['num1', '1 decimal'],
  ['num2', '2 decimals'],
  ['pct', 'Percent of a fraction'],
  ['days', 'Days'],
  ['nights', 'Nights'],
];

const LOOK_KEYS = [...new Set([...CHOICES.filter((entry) => entry.group !== 'data' && !entry.grained).map((entry) => entry.key), 'size', 'h', 'xLabels', 'xRange', 'xTick', 'xTitle', 'yTitle', 'scale', 'refs'])];

function LookCopy({ block, kind, siblings, editor }: { block: Block; kind?: string; siblings: Array<Block>; editor: BoardEditor }) {
  const others = siblings.filter((other) => other.id !== block.id && kind && blockKind(other) === kind);
  if (!others.length) return null;
  const patch = Object.fromEntries(LOOK_KEYS.map((key) => [key, block[key] ?? null]));
  return (
    <Box>
      <Button
        disabled={editor.pending}
        onClick={() => editor.apply(others.flatMap((other) => [{ op: 'set' as const, id: other.id, patch: Object.fromEntries(LOOK_KEYS.map((key) => [key, null])) }, { op: 'set' as const, id: other.id, patch }]))}
        size="small"
        sx={{ textTransform: 'none' }}
        variant="text"
      >
        {`Use this block’s look on the other ${others.length} like it`}
      </Button>
    </Box>
  );
}

const DATA_FAMILIES: Array<BlockFamily | undefined> = ['figures', 'time', 'breakdown', 'bridge', 'table'];

const WIDTHS: Array<[string, string, string]> = [
  [INHERIT, 'Auto', 'Share the row'],
  ...SPANS.map(([span, label]): [string, string, string] => [String(span), { 3: '¼', 4: '⅓', 6: '½', 8: '⅔', 12: 'Full' }[span] ?? label, label]),
];

function membersOf(block: Block, data: LensDocumentData | undefined, column: string): Array<string> {
  const planned = data?.blocks[block.id];
  const rows = (planned?.queries ?? (planned?.query ? [planned.query] : [])).flatMap((id) => data?.queries[id]?.rows ?? []);
  return [...new Set(rows.map((row) => row[column]).filter((value) => value !== undefined && value !== null).map(String))].slice(0, 200);
}

function blockColumns(block: Block, data?: LensDocumentData): Array<EnvelopeColumn> {
  const planned = data?.blocks[block.id];
  return (planned?.queries ?? (planned?.query ? [planned.query] : [])).flatMap((id) => data?.queries[id]?.columns ?? []);
}

function Choices({ label, value, choices, keywords, disabled, onChange }: { label: string; value: string; choices: Array<[string, string, string?]>; keywords?: Array<string>; disabled: boolean; onChange: (value: string) => void }) {
  return <Choice choices={choices} disabled={disabled} inherit={choices.some(([option]) => option === INHERIT) ? INHERIT : undefined} keywords={keywords} label={label} onChange={onChange} value={value} />;
}

type Item = string | KpiItem;

const itemName = (entry: Item) => (typeof entry === 'string' ? entry : entry.kpi);

const columnFor = (columns: Array<EnvelopeColumn>, name: string) =>
  columns.find((column) => column.name === name || name.endsWith(`.${column.name}`) || column.name.endsWith(`.${name}`));

function itemsOf(block: Block): { key: 'kpis' | 'table'; items: Array<Item> } | undefined {
  if (Array.isArray(block.kpis)) return { key: 'kpis', items: block.kpis as Array<Item> };
  if (Array.isArray(block.table)) return { key: 'table', items: block.table as Array<Item> };
  return undefined;
}

const PART_LIMIT = 12;

function partsOf(block: Block, family: BlockFamily, data?: LensDocumentData): Array<[string, string]> {
  const planned = data?.blocks[block.id];
  const envelope = data?.queries[planned?.queries?.[0] ?? planned?.query ?? ''];
  if (!envelope) return [];
  const dim = envelope.columns.find((column) => column.role === 'dim');
  const measureColumns = envelope.columns.filter((column) => column.role === 'measure');
  const rows = envelope.rows.filter((row) => row.level === undefined || row.level === 1);
  const membersOf = (shown: typeof rows) => (dim ? [...new Set(shown.map((row) => String(row[dim.name])))].slice(0, PART_LIMIT) : []);
  const members = membersOf(rows);
  const kind = blockKind(block);
  if (kind === 'bridge' || kind === 'scatter') return [['@axis', 'Axis']];
  if (family === 'breakdown' || kind === 'table') return membersOf(rows.filter((row) => !row.other)).map((member) => [`member:${member}`, memberLabel(member)]);
  if (family !== 'time') return [];
  const series: Array<[string, string]> = dim ? members.map((member) => [`series:${member}`, memberLabel(member)]) : measureColumns.map((column) => [`series:${column.name}`, column.label]);
  if (kind === 'calendar') return series;
  const compared: Array<[string, string]> = dim ? [] : Object.keys(envelope.compare).map((id) => [`series:${id}`, id === 'ly' ? 'Last year' : id === 'l2y' ? 'Two years ago' : 'Prior period']);
  return [['@axis', 'Axis'], ...series, ...compared];
}

const FIGURE_CHOICES: Array<{ key: 'spark' | 'status' | 'delta' | 'baseline' | 'numbers'; label: string; choices: Array<[string, string]> }> = [
  {
    key: 'spark',
    label: 'Show trend line',
    choices: [
      [INHERIT, 'Block'],
      ['false', 'Hide'],
    ],
  },
  {
    key: 'status',
    label: 'Status colour',
    choices: [
      [INHERIT, 'Block'],
      ['border', 'On'],
      ['none', 'Off'],
    ],
  },
  {
    key: 'delta',
    label: 'Change as',
    choices: [
      [INHERIT, 'Block'],
      ['pct', '%'],
      ['diff', 'Difference'],
      ['both', 'Both'],
      ['none', 'Off'],
    ],
  },
  {
    key: 'baseline',
    label: 'Compared values',
    choices: [
      [INHERIT, 'Block'],
      ['true', 'Show'],
      ['false', 'Hide'],
    ],
  },
  {
    key: 'numbers',
    label: 'Numbers',
    choices: [
      [INHERIT, 'Block'],
      ['compact', 'A$28.4M'],
      ['full', 'In full'],
    ],
  },
];

function ItemSettings({ block, name, data, editor, onSelect }: { block: Block; name: string; data?: LensDocumentData; editor: BoardEditor; onSelect?: (id: string) => void }) {
  const fields = useGetReportingLensFields(data?.lensId ?? '', data?.lensVersion);
  const list = itemsOf(block);
  const entry = list?.items.find((candidate) => itemName(candidate) === name);
  const column = columnFor(blockColumns(block, data), name);
  const [swappingTo, setSwappingTo] = useState<string>();
  const arrived = Boolean(swappingTo && list?.items.some((candidate) => itemName(candidate) === swappingTo));
  useEffect(() => {
    if (!arrived || !swappingTo) return;
    onSelect?.(`${block.id}/${swappingTo}`);
    setSwappingTo(undefined);
  }, [arrived, block.id, onSelect, swappingTo]);
  if (!list || !entry) return null;
  const known: Array<[string, string]> = [
    ...(fields.data?.measures ?? []).map((measure): [string, string] => [measure.id, measure.label]),
    ...(fields.data?.datasets ?? []).flatMap((dataset) => dataset.certified.map((measure): [string, string] => [measure.id, measure.label])),
  ].filter(([id], index, all) => all.findIndex(([other]) => other === id) === index);
  const others = known.filter(([id]) => id !== name);
  const taken = new Set(list.items.map(itemName).filter((other) => other !== name));
  const showable: Array<[string, string]> = known.some(([id]) => id === name) ? known : [[name, column?.label ?? name], ...known];
  const path = `${block.id}/${name}`;
  const index = list.items.indexOf(entry);
  const neighbour = (offset: number) => list.items[index + offset];
  const tile: KpiItem = typeof entry === 'string' ? { kpi: entry } : entry;
  const figure = list.key === 'kpis';
  const set = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: path, patch });
  const move = (offset: number) => {
    const anchor = `${block.id}/${itemName(neighbour(offset))}`;
    editor.apply(offset < 0 ? { op: 'move', id: path, before: anchor } : { op: 'move', id: path, after: anchor });
  };
  const back = () => onSelect?.(block.id);
  const act = (label: string, icon: React.ReactNode, run: () => void, off: boolean, danger = false) => (
    <Tooltip title={label}>
      <span>
        <IconButton aria-label={label} color={danger ? 'error' : 'default'} disabled={editor.pending || off} onClick={run} size="small">
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
  return (
    <Box data-lens-item-settings={path} sx={{ px: 2, pb: 2, pt: 1.5 }}>
      <PanelHeader back={String(block.title ?? data?.blocks[block.id]?.name ?? 'Block')} onBack={back} title={tile.label ?? column?.label ?? name}>
        {act('Move left', <ChevronLeftIcon fontSize="small" />, () => move(-1), !neighbour(-1))}
        {act('Move right', <ChevronRightIcon fontSize="small" />, () => move(1), !neighbour(1))}
        {figure
          ? act(
              'Own block',
              <OpenInNewIcon fontSize="small" />,
              () => {
                editor.apply({ op: 'move', id: path, after: block.id });
                back();
              },
              list.items.length < 2,
            )
          : null}
        {act(
          figure ? 'Remove' : 'Hide',
          figure ? <DeleteOutlineIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />,
          () => {
            editor.apply({ op: 'remove', id: path });
            back();
          },
          list.items.length < 2,
          true,
        )}
      </PanelHeader>
      <SettingsFind>
        <Section defaultOpen id={figure ? 'figure' : 'column'} title={figure ? 'Figure' : 'Column'}>
          <Row keywords={showable.map(([, label]) => label)} label="Metric">
            <Picker
              choices={showable.map(([id, label]) => [id, label, taken.has(id)])}
              disabled={editor.pending || fields.isLoading}
              label="Metric"
              onChange={(next) => {
                editor.apply({ op: 'set', id: path, patch: { kpi: next } });
                setSwappingTo(next);
              }}
              value={name}
            />
          </Row>
          {figure ? <TextSetting label="Figure label" onCommit={(label) => set({ label: label || null })} placeholder={column?.label ?? name} value={tile.label ?? ''} /> : null}
        </Section>
        {figure ? (
          <Section defaultOpen id="figure-style" title="Figure style">
              <Choices
                choices={[
                  [INHERIT, 'Regular'],
                  ['lg', 'Large'],
                ]}
                disabled={editor.pending}
                label="Size"
                onChange={(value) => set({ size: value === INHERIT ? null : value })}
                value={tile.size ?? INHERIT}
              />
              {FIGURE_CHOICES.map((entry) => (
                <Choices
                  choices={entry.choices}
                  disabled={editor.pending}
                  key={entry.key}
                  keywords={entry.key === 'spark' ? ['sparkline', 'spark'] : []}
                  label={entry.label}
                  onChange={(value) => set({ [entry.key]: value === INHERIT ? null : value === 'false' ? false : value === 'true' ? true : value })}
                  value={tile[entry.key] === undefined || (entry.key === 'spark' && (tile.spark as unknown) === true) ? INHERIT : String(tile[entry.key])}
                />
              ))}
          </Section>
        ) : null}
        {figure ? (
          <Section id="figure-against" summary={[tile.target !== undefined ? 'target' : '', tile.forecast ? 'forecast' : '', tile.against?.length ? 'compared' : ''].filter(Boolean).join(', ')} title="Targets and comparisons">
            <TextSetting
              label="Target"
              onCommit={(target) => set({ target: target === '' ? null : Number.isFinite(Number(target)) ? Number(target) : target })}
              placeholder="A number, or a measure id"
              value={tile.target === undefined ? '' : String(tile.target)}
            />
            <TextSetting label="Forecast" onCommit={(forecast) => set({ forecast: forecast || null })} placeholder="A measure id" value={tile.forecast ?? ''} />
            <Row keywords={others.map(([, label]) => label)} label="Also compare with" set={Boolean(tile.against?.length)}>
              <Picker choices={[['', 'Nothing else'], ...others]} disabled={editor.pending} label="Also compare with" onChange={(kpi) => set({ against: kpi ? [{ kpi }] : null })} value={tile.against?.[0]?.kpi ?? ''} />
            </Row>
          </Section>
        ) : null}
        {column ? (
          <Section id="measure-lens-wide" summary={column.label} title="Measure, Lens-wide">
            <MeasureSettings column={column} editor={editor} />
          </Section>
        ) : null}
      </SettingsFind>
    </Box>
  );
}

function memberBound(text: string): number | null {
  const plain = text.replace(/[\s,]/g, '');
  const value = Number(plain.replace(/%$/, ''));
  return plain && Number.isFinite(value) ? (plain.endsWith('%') ? value / 100 : value) : null;
}

export default function BlockSettings({ block, kind, family, data, editor, item, view, siblings = [], onSelect, state, lens, howItsMade }: BlockSettingsProps) {
  const presentation = usePanePresentation();
  if (block && item && /^(series:|member:|dim:|@axis$)/.test(item)) return <PartSettings block={block} data={data} editor={editor} onSelect={onSelect} part={item} />;
  if (block && item && !item.startsWith('@')) return <ItemSettings block={block} data={data} editor={editor} name={item} onSelect={onSelect} />;
  if (!block)
    return (
      <Stack data-lens-settings-panel spacing={1} sx={{ color: 'text.secondary', p: 2 }}>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>
          Select a block on the board, or add one.
        </Typography>
        <AddBlockButton data={data} document={lens} editor={editor} />
      </Stack>
    );
  const set = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: block.id, patch } as BoardOperation);
  const current = (key: string) => {
    const value = block[key];
    if (key === 'spark' && (value === true || value === 13)) return INHERIT;
    if (key === 'spark' && value === false) return '0';
    return value === undefined || value === null ? INHERIT : String(value);
  };
  const choice = (entry: Choice) => (
    <Choices
      choices={entry.choices}
      disabled={editor.pending}
      key={entry.key}
      keywords={entry.keywords}
      label={entry.label}
      onChange={(value) => {
        const cleared = value === INHERIT ? {} : Object.fromEntries((entry.clears ?? []).map((key) => [key, null]));
        set({ ...cleared, ...(entry.key === 'size' ? { h: null } : {}), [entry.key]: value === INHERIT ? null : entry.parse ? entry.parse(value) : value === 'false' ? false : value === 'true' ? true : value });
      }}
      value={current(entry.key)}
    />
  );
  const choicesIn = (group: Group) =>
    CHOICES.filter((entry) => entry.group === group && (!entry.families || (family && entry.families.includes(family))) && !(entry.grained && block.axis) && (kind !== 'calendar' || ['delta', 'baseline', 'numbers', 'select', 'subtitle', 'size', 'titleAlign', 'titleSize', 'frame'].includes(entry.key))).map(choice);
  const forms = DATA_FAMILIES.includes(family) && kind ? suitableForms(kind, blockColumns(block, data)) : [];
  const measures = blockMeasures(block, data);
  const topped = kind !== 'calendar' && family && TOPPED.includes(family);
  const parts = family ? partsOf(block, family, data) : [];

  const changed = (group: Group) =>
    CHOICES.filter((entry) => entry.group === group && current(entry.key) !== INHERIT && (!entry.families || (family && entry.families.includes(family))))
      .map((entry) => `${entry.label.toLowerCase()} ${entry.choices.find(([option]) => option === current(entry.key))?.[1].toLowerCase() ?? current(entry.key)}`)
      .join(', ');
  const items = itemsOf(block);

  const appearance = (
        <Section id="appearance" summary={[block.w ? `width ${WIDTHS.find(([option]) => option === String(block.w))?.[1] ?? `${Math.round((Number(block.w) / 12) * 100)}%`}` : '', changed('appearance'), block.note ? 'footnote' : '', block.description ? 'description' : ''].filter(Boolean).join(', ')} title="Appearance">
          <SnapSlider
            disabled={editor.pending}
            label="Width"
            marks={[0, 3, 4, 6, 8, 9, 12]}
            max={12}
            min={0}
            onCommit={(span) => set({ w: span < 1 ? null : Math.max(2, span) })}
            onReset={() => set({ w: null })}
            read={(text) => (text.trim() ? Math.round((Number(text.replace('%', '')) / 100) * 120) / 10 || 0 : 0)}
            set={Boolean(block.w)}
            show={(span) => (span < 1 ? '' : String(Math.round((span / 12) * 100)))}
            snap={0.3}
            step={0.1}
            unit="%"
            value={typeof block.w === 'number' ? block.w : 0}
          />
          {SIZED.includes(family) ? (
            <SnapSlider
              disabled={editor.pending}
              label="Height"
              marks={presetsFor(family).map(([, px]) => px)}
              max={800}
              min={family === 'figures' ? 0 : minContentFor(family)}
              onCommit={(px) => { const preset = presetsFor(family).find(([, at]) => at === px); set(family === 'figures' && px < minContentFor(family) ? { h: null } : heightPatch(family, preset ? { size: preset[0] } : { h: px })); }}
              onReset={() => set(heightPatch(family, {}))}
              read={(text) => Math.round(Number(text.replace(/px$/i, '')) || (family === 'figures' ? 0 : 300))}
              set={Boolean(block.h || (family !== 'figures' && block.size))}
              show={(px) => (px ? String(px) : '')}
              snap={12}
              step={10}
              unit="px"
              value={typeof block.h === 'number' ? block.h : family === 'figures' ? 0 : PRESETS.find(([size]) => size === block.size)?.[1] ?? 300}
            />
          ) : null}
          {choicesIn('appearance')}
          <SizeSetting
            disabled={editor.pending}
            label="Title size"
            named={[{ value: 'small', label: 'Small' }, { value: null, label: 'Regular' }, { value: 'large', label: 'Large' }]}
            onChange={(next) => set({ titleSize: next })}
            pixels={[12, 14, 16, 18, 20, 24, 28, 32]}
            range={[10, 36]}
            value={(block.titleSize as string | number | undefined) ?? null}
          />
          <TextSizeSetting disabled={editor.pending} inherit="Lens" onChange={(next) => set({ textSize: next })} value={block.textSize} />
          <TextSetting label="Footnote" multiline onCommit={(note) => set({ note: note || null })} value={typeof block.note === 'string' ? block.note : ''} />
          <TextSetting label="Description readers can open" multiline onCommit={(description) => set({ description: description || null })} placeholder="How this is counted, what it leaves out" value={typeof block.description === 'string' ? block.description : ''} />
        </Section>
  );
  const dataSection = topped || choicesIn('data').length || (onSelect && (parts.length || items)) ? (
          <Section id="data" summary={[block.top ? `top ${block.top}` : '', block.other === false ? 'no other' : '', changed('data')].filter(Boolean).join(', ')} title={items?.key === 'kpis' ? 'Figures' : kind === 'table' ? 'Rows and columns' : family === 'time' ? 'Axis and series' : family === 'breakdown' ? 'Members' : 'Rows'}>
            {topped ? (
              <Row label="Show top" set={Boolean(block.top) || block.other === false}>
                <Box sx={{ width: 72 }}>
                  <TextInput label="Show top" onCommit={(top) => set({ top: Number(top) > 0 ? Math.round(Number(top)) : null })} placeholder="All" value={block.top ? String(block.top) : ''} />
                </Box>
                <FormControlLabel
                  control={<Switch checked={block.other !== false} disabled={editor.pending} onChange={(event) => set({ other: event.target.checked ? null : false })} size="small" />}
                  label="Other"
                  slotProps={{ typography: { color: 'text.secondary', fontSize: 12.5 } }}
                />
              </Row>
            ) : null}
            {topped ? (
              <Row keywords={['at least', 'at most', 'threshold', 'minimum', 'maximum']} label="Only members with" set={typeof block.atLeast === 'number' || typeof block.atMost === 'number'}>
                {(['atLeast', 'atMost'] as const).map((side) => (
                  <Box key={side} sx={{ width: 88 }}>
                    <TextInput label={side === 'atLeast' ? 'At least' : 'At most'} onCommit={(text) => set({ [side]: memberBound(text) })} placeholder={side === 'atLeast' ? 'at least' : 'at most'} value={typeof block[side] === 'number' ? String(block[side]) : ''} />
                  </Box>
                ))}
              </Row>
            ) : null}
            {choicesIn('data')}
            {onSelect && family && parts.length ? (
              <Row label={family === 'time' ? 'Axis and series' : kind === 'table' ? 'Rows' : family === 'breakdown' ? 'Members' : 'Axis'} stacked>
                <PartList onSelect={(part) => onSelect(`${block.id}/${part}`)} parts={parts} />
              </Row>
            ) : null}
            {kind === 'table' ? <TextSetting label="Total row label" onCommit={(totalLabel) => set({ totalLabel: totalLabel || null })} placeholder="All" value={typeof block.totalLabel === 'string' ? block.totalLabel : ''} /> : null}
            {kind === 'table' || family === 'breakdown' ? <ValuePills block={block} columns={blockColumns(block, data).filter((column) => column.role !== 'time')} editor={editor} members={(column) => membersOf(block, data, column)} /> : null}
            {kind === 'table' ? <ColumnGroups block={block} editor={editor} measures={measures} /> : null}
            {items && onSelect ? (
              <Row label={items.key === 'kpis' ? 'Figures' : 'Columns'} stacked>
                <PartList onSelect={(name) => onSelect(`${block.id}/${name}`)} parts={items.items.map((entry) => [itemName(entry), columnFor(blockColumns(block, data), itemName(entry))?.label ?? itemName(entry)])} />
              </Row>
            ) : null}
          </Section>
        ) : null;
  const colourColumns = blockColumns(block, data);
  const colourable = ['line', 'area', 'column', 'combo', 'calendar', 'bar', 'rank', 'pie'].includes(kind ?? '') &&
    (family !== 'breakdown' || (colourColumns.filter((column) => column.role === 'dim').length <= 1 && colourColumns.filter((column) => column.role === 'measure').length === 1));
  const colourParts = colourable ? parts.filter(([part]) => part.startsWith('series:') || part.startsWith('member:')) : [];
  const colourSettings = colourParts.length ? (
    <Section defaultOpen id="chart-colours" title="Colour">
      <SeriesColours block={block} editor={editor} key={block.id} parts={colourParts} />
    </Section>
  ) : null;
  const between = (
    <>
      {colourSettings}
      {appearance}
      {dataSection}
    </>
  );

  return (
    <Box data-lens-settings-panel sx={{ px: 2, pb: 2, pt: 1.5 }}>
      <Stack alignItems="center" data-setting-key="root:Title" data-setting-row="Title" data-setting-section="data" direction="row" spacing={0.5} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextInput label="Title" onCommit={(title) => set({ title: title || null })} placeholder={data?.blocks[block.id]?.name ?? 'Untitled'} shown={typeof block.title === 'string' && block.title.includes('{$') && lens ? plainTitle(block.title, lens, data) : undefined} size="title" value={typeof block.title === 'string' ? block.title : ''} />
        </Box>
      </Stack>
      <SettingsFind>
        {family === 'prose' && kind ? (
          <TextRow
            label="Text"
            multiline
            onCommit={(value) => value && set({ [kind]: value })}
            placeholder="Write {measure} for a live number, {measure vs ly} for its change, **bold** to stress"
            value={String(block[kind] ?? '')}
          />
        ) : null}
        {kind === 'text' ? <TextRow label="Question" multiline onCommit={(ask) => set({ ask: ask || null })} placeholder="Ask about the current board" value={typeof block.ask === 'string' ? block.ask : ''} /> : null}
        {kind === 'view' && typeof block.view === 'string' ? (
          <Section defaultOpen id="code" title="Code">
            <ViewCode editor={editor} id={block.view} view={view} />
            {lens ? <ReadsQueries block={block} editor={editor} lens={lens} /> : null}
          </Section>
        ) : null}
        {forms.length > 1 || choicesIn('chart').length || kind === 'kpi' ? (
          <Section id="chart" summary={changed('chart')} title="Chart">
            {kind === 'kpi' ? (
              <Choices
                choices={[
                  [INHERIT, 'Figure'],
                  ['bullet', 'Against a target'],
                ]}
                disabled={editor.pending}
                label="Drawn as"
                onChange={(form) => set({ form: form === INHERIT ? null : form })}
                value={typeof block.form === 'string' ? block.form : INHERIT}
              />
            ) : null}
            {forms.length > 1 && kind ? (
              <Choices choices={forms} disabled={editor.pending} label="Show as" onChange={(form) => editor.apply({ op: 'form', id: block.id, kind: form })} value={kind} />
            ) : null}
            {choicesIn('chart')}
            <LookCopy block={block} editor={editor} kind={kind} siblings={siblings} />
          </Section>
        ) : null}
        {DATA_FAMILIES.includes(family) && kind ? (
          <>
            <QuerySettings between={presentation ? undefined : between} block={block} data={data} editor={editor} family={family} kind={kind} namedQueries={Object.keys(lens?.queries ?? {})} params={lens?.params} state={state} />
            {presentation ? between : null}
          </>
        ) : between}
        {measures.length ? (
          <Section id="measures" summary={measures.map((column) => column.label).join(', ')} title="Measures, Lens-wide">
            {measures.map((column) => (
              <MeasureSettings column={column} editor={editor} key={column.name} />
            ))}
          </Section>
        ) : null}
        {lens && DATA_FAMILIES.includes(family) && (lens.filters.length || Object.keys(lens.params).length) ? (
          <Section id="reach" summary={[Array.isArray(block.ignore) && block.ignore.length ? `ignores ${block.ignore.length}` : '', block.when ? 'shown on a switch' : ''].filter(Boolean).join(', ')} title="Lens filters and switches">
            <FollowsFilters block={block} editor={editor} labelOf={(column) => blockColumns(block, data).find((entry) => entry.name === column)?.label ?? humanise(column)} lens={lens} />
            <ShownWhen block={block} editor={editor} lens={lens} />
          </Section>
        ) : null}
        <Section id="every" summary="Expert spec editor" title="Every setting">
          <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>Raw Lens spec fields. Use the named controls above for everyday changes.</Typography>
          <RawSettings disabled={editor.pending} inline onSet={(key, value) => editor.apply(replaceSetting(block.id, key, value))} skip={['id']} title="Every setting" values={block} />
        </Section>
        {howItsMade}
      </SettingsFind>
      <Stack direction="row" spacing={1} sx={{ borderTop: 1, borderColor: 'divider', mt: 1, pt: 1.5 }}>
        <Button
          disabled={editor.pending}
          onClick={() => {
            const { id, ...copy } = block;
            editor.apply({ op: 'add', block: copy, after: id });
          }}
          size="small"
          startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
          variant="text"
        >
          Duplicate
        </Button>
        <Button color="inherit" disabled={editor.pending} onClick={() => editor.apply({ op: 'remove', id: block.id })} size="small" startIcon={<DeleteOutlineIcon fontSize="small" />} variant="text">
          Delete
        </Button>
      </Stack>
    </Box>
  );
}

function PartList({ parts, onSelect }: { parts: Array<[string, string]>; onSelect: (part: string) => void }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {parts.map(([part, label]) => (
        <Chip clickable key={part} label={label} onClick={() => onSelect(part)} size="small" sx={{ fontSize: 12.5 }} variant="outlined" />
      ))}
    </Stack>
  );
}
